<template>
  <!--
    WHAT: Right-side slide-over drawer to EDIT an existing DataAttribute or CREATE
          a new one (name, role, description, attribute_type on create, and the
          SQL query). Loads the DA on mount, tracks dirty state, and PATCHes/POSTs to
          the attributes endpoint.
    WHERE: [reused verbatim from v1] Opened FROM StudioJoinBuilder (its "✎ edit"
           per-step button and "＋ Create new" in the library drawer). Not rendered
           directly by a section.
    CONTRACT:
      props: attributeId (Number, default null) — pass null / omit to CREATE.
      emits: close, saved(dataAttribute)  — `saved` returns the persisted DA so
             StudioJoinBuilder can refresh its column cache / append a new DA.
    NOTES: [reused verbatim from v1] shared component — do NOT restyle without
           checking other callers. Gotchas: create vs edit is inferred purely from
           attributeId == null; SQL save errors are surfaced from the server's
           sql_query validation field; a fresh create seeds a BLANK_FORM SQL stub
           with {tenant_id}.
  -->
  <div class="fixed inset-0 z-40">
    <div class="absolute inset-0 bg-black/20" @click="$emit('close')"></div>
    <aside class="absolute top-0 right-0 h-full w-[720px] max-w-full bg-white shadow-xl flex flex-col">

      <!-- Header -->
      <header class="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
        <div class="flex items-center gap-2 min-w-0">
          <span v-if="form" class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-xs" :class="roleBadgeClass">
            <span class="w-1 h-1 rounded-full" :class="roleDotClass"></span>
            {{ form.role || 'unset' }}
          </span>
          <span v-if="form && form.id" class="text-xs text-gray-400">#{{ form.id }}</span>
          <span v-if="form && !isCreate" class="text-xs text-gray-800 truncate">{{ form.name }}</span>
          <span v-else-if="form && isCreate" class="text-xs font-semibold text-gray-800">New attribute</span>
          <span v-else class="text-xs text-gray-400">Loading…</span>
        </div>
        <button type="button" class="text-gray-400 hover:text-gray-700 text-lg leading-none" @click="$emit('close')">&times;</button>
      </header>

      <div v-if="loading" class="flex-1 flex items-center justify-center text-xs text-gray-400">Loading…</div>
      <div v-else-if="loadError" class="flex-1 p-4 text-xs text-red-600">{{ loadError }}</div>

      <template v-else-if="form">
        <!-- Fields -->
        <div class="px-4 py-3 border-b border-gray-100 grid grid-cols-2 gap-3">
          <div>
            <label class="block text-[10px] uppercase tracking-wider font-semibold text-gray-500 mb-1">Name</label>
            <input v-model="form.name" type="text" class="w-full text-xs px-2.5 py-1.5 border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-300" />
          </div>
          <div>
            <label class="block text-[10px] uppercase tracking-wider font-semibold text-gray-500 mb-1">Role</label>
            <select v-model="form.role" class="w-full text-xs px-2.5 py-1.5 border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-300">
              <option value="">— unset —</option>
              <option value="date_spine">Date Spine</option>
              <option value="population">Population</option>
              <option value="denominator">Denominator</option>
              <option value="intervention">Intervention</option>
              <option value="metadata">Metadata</option>
              <option value="opportunity">Opportunity</option>
            </select>
          </div>
          <div v-if="isCreate">
            <label class="block text-[10px] uppercase tracking-wider font-semibold text-gray-500 mb-1">Attribute Type</label>
            <select v-model="form.attribute_type" class="w-full text-xs px-2.5 py-1.5 border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-300">
              <option value="categorical">Categorical (Binary)</option>
              <option value="continuous">Continuous</option>
              <option value="multi_categorical">Multi-Categorical</option>
              <option value="date">Date</option>
            </select>
          </div>
          <div :class="isCreate ? '' : 'col-span-2'">
            <label class="block text-[10px] uppercase tracking-wider font-semibold text-gray-500 mb-1">Description</label>
            <input v-model="form.description" type="text" class="w-full text-xs px-2.5 py-1.5 border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-300" />
          </div>

        </div>

        <!-- SQL Editor -->
        <div class="flex-1 flex flex-col px-4 py-3 min-h-0">
          <label class="block text-[10px] uppercase tracking-wider font-semibold text-gray-500 mb-1">SQL Query</label>
          <textarea
            v-model="form.sql_query"
            spellcheck="false"
            class="flex-1 w-full text-[11px] font-mono px-3 py-2 border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-300 leading-relaxed resize-none"
          ></textarea>
        </div>
      </template>

      <!-- Footer -->
      <footer class="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
        <div class="flex items-center gap-3">
          <label v-if="form" class="inline-flex items-center gap-1.5 text-xs text-gray-600">
            <input v-model="form.is_active" type="checkbox" class="w-3.5 h-3.5" />
            Active
          </label>
          <span v-if="form && form.verified_at" class="text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded">
            verified {{ form.verified_at.slice(0, 10) }}
          </span>
        </div>
        <div class="flex items-center gap-2">
          <span v-if="error" class="text-xs text-red-600 truncate max-w-[200px]">{{ error }}</span>
          <span v-else-if="dirty" class="text-xs text-gray-500">Unsaved</span>
          <button type="button" class="text-xs text-gray-500 hover:text-gray-700 px-2 py-1" @click="$emit('close')">Cancel</button>
          <button
            type="button"
            class="text-xs text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 px-3 py-1 rounded"
            :disabled="!dirty || saving"
            @click="save"
          >{{ saving ? 'Saving…' : 'Save' }}</button>
        </div>
      </footer>
    </aside>
  </div>
</template>

<script>
import axios from 'axios';

const ROLE_BADGE = {
  date_spine:   { badge: 'bg-purple-50 text-purple-800 border-purple-200', dot: 'bg-purple-500' },
  population:   { badge: 'bg-blue-50 text-blue-800 border-blue-200', dot: 'bg-blue-500' },
  denominator:  { badge: 'bg-amber-50 text-amber-800 border-amber-200', dot: 'bg-amber-500' },
  intervention: { badge: 'bg-emerald-50 text-emerald-800 border-emerald-200', dot: 'bg-emerald-500' },
};

const BLANK_FORM = {
  id: null, name: '', description: '', role: '', attribute_type: 'categorical',
  sql_query: 'SELECT\n  -- columns here\nFROM ...\nWHERE TENANT_ID = {tenant_id}',
  is_active: true, verified_at: null,
};

export default {
  name: 'PrimitiveEditorDrawer',
  props: {
    // omit / pass null to CREATE a new DataAttribute
    attributeId: { type: Number, default: null },
  },
  emits: ['close', 'saved'],
  data() {
    return {
      loading: true,
      loadError: null,
      original: null,
      form: null,
      saving: false,
      error: null,
    };
  },

  computed: {
    isCreate() { return this.attributeId == null; },
    roleBadgeClass() { return ROLE_BADGE[this.form?.role]?.badge || 'bg-gray-50 text-gray-700 border-gray-200'; },
    roleDotClass() { return ROLE_BADGE[this.form?.role]?.dot || 'bg-gray-400'; },
    dirty() {
      if (!this.form || !this.original) return false;
      return JSON.stringify(this.form) !== JSON.stringify(this.original);
    },
  },
  async mounted() {
    if (this.isCreate) {
      this.original = JSON.parse(JSON.stringify(BLANK_FORM));
      this.form = JSON.parse(JSON.stringify(BLANK_FORM));
      this.loading = false;
      return;
    }
    try {
      const { data } = await axios.get(`api/impact_analytics/attributes/${this.attributeId}/`);
      this.original = JSON.parse(JSON.stringify(data));
      this.form = JSON.parse(JSON.stringify(data));
    } catch (e) {
      this.loadError = e?.response?.data?.detail || e.message;
    } finally {
      this.loading = false;
    }
  },
  methods: {
    async save() {
      this.saving = true;
      this.error = null;
      try {
        const payload = {
          name: this.form.name,
          description: this.form.description,
          role: this.form.role || null,
          sql_query: this.form.sql_query,
          is_active: this.form.is_active,
        };
        if (this.isCreate) payload.attribute_type = this.form.attribute_type || 'categorical';
        const { data } = this.isCreate
          ? await axios.post('api/impact_analytics/attributes/', payload)
          : await axios.patch(`api/impact_analytics/attributes/${this.form.id}/`, payload);
        this.original = JSON.parse(JSON.stringify(data));
        this.form = JSON.parse(JSON.stringify(data));
        this.$emit('saved', data);
      } catch (e) {
        this.error = e?.response?.data?.sql_query?.[0]
          || e?.response?.data?.detail
          || (typeof e?.response?.data === 'object' ? JSON.stringify(e.response.data) : null)
          || e.message;
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
